import {cva, type VariantProps} from 'cva';
import type {ComponentProps, ElementRef, Ref} from 'react';
import React from 'react';
import {PolyElement, createPolymorphicComponent} from '../../Utilities/createPolymorphicComponent';

function RsnButton({className, small, intent, type = 'button', buttonRef, ...restProps}: RsnButton.Props) {
    return (
        <button
            {...restProps}
            ref={buttonRef}
            type={type}
            className={variants({intent, small, className})}
            //
        />
    );
}

namespace RsnButton {
    export type Intent = Props['intent'];

    export interface OwnProps extends VariantProps<typeof variants> {
        className?: string;
    }

    export interface Props extends ComponentProps<'button'>, OwnProps {
        type?: 'button' | 'submit';
        buttonRef?: Ref<ElementRef<'button'>>;
    }
}

type Props2 = Omit<RsnButton.Props, 'intent'>;
/**
 * Blue bg / White text
 */
const Primary = (props: Props2) => <RsnButton {...props} intent='primary' />;
Primary.displayName = 'RsnButton.Primary';

/**
 * Lightblue bg / Blue text
 */
const Secondary = (props: Props2) => <RsnButton {...props} intent='secondary' />;
Secondary.displayName = 'RsnButton.Secondary';

/**
 * Gray bg / Blue text
 */
const Tertiary = (props: Props2) => <RsnButton {...props} intent='tertiary' />;
Tertiary.displayName = 'RsnButton.Tertiary';

/**
 * Pink bg / Red text
 */
const Cautionary = (props: Props2) => <RsnButton {...props} intent='cautionary' />;
Cautionary.displayName = 'RsnButton.Cautionary';

const Alike = createPolymorphicComponent('button')<RsnButton.OwnProps>(({intent, small, className, ...props}) => (
    <PolyElement {...props} className={variants({intent, small, className})} />
));
Alike.displayName = 'RsnButton.Alike';

/**
 * Gold bg / Black text
 */
const Gold = (props: Props2) => <RsnButton {...props} intent='gold' />;
Gold.displayName = 'RsnButton.Gold';

RsnButton.Primary = Primary;
RsnButton.Secondary = Secondary;
RsnButton.Tertiary = Tertiary;
RsnButton.Cautionary = Cautionary;
RsnButton.Alike = Alike;
RsnButton.Gold = Gold;

export const variants = cva('RsnButton', {
    variants: {
        intent: {
            primary: 'RsnButton--Primary',
            gold: 'RsnButton--Gold',
            secondary: 'RsnButton--Secondary',
            tertiary: 'RsnButton--Tertiary',
            cautionary: 'RsnButton--Cautionary',
        },
        small: {
            true: 'RsnButton--Small',
            false: '',
        },
    },
    defaultVariants: {
        intent: 'primary',
        small: false,
    },
});

export default RsnButton;
